import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button as ButtonBase, Empty, Flex, Spin } from 'antd'
import { FunctionComponent, ReactNode, useState } from 'react'
import { useWindowSize } from 'react-use'
import styled, { css, FlattenSimpleInterpolation } from 'styled-components'
import { Duration } from '@/components/common/duration-select'
import { KolThumbnail } from '@/components/common/kol-thumbnail'
import { initialPostFilterConditions } from '@/components/kol/detail/creative-content-analyses/post-list'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import useMentionedKols from '@/components/kol/hooks/use-mentioned-kols'
import { SafeNewTabLink } from '@/components/safe-new-tab-link'
import { MOCK_KOL_INFO_PREFIX } from '@/constants/mock-data/kol-info'
import routes from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import { ampli } from '@/utils/ampli'
import { PlatformShortcode } from '@/utils/convert-platform'
import getDateRangeFromETLAt from '@/utils/get-date-range-from-etl-at'

interface MentionedKolListProps {
  kolId: string
  kolDcIdHashed?: string
  kolName: string
  platform: PlatformShortcode
  duration: Duration.ThreeMonths | Duration.SixMonths
  showMockData?: boolean
}

const MentionedKolList: FunctionComponent<MentionedKolListProps> = ({
  kolId,
  kolDcIdHashed,
  kolName,
  platform,
  duration,
  showMockData = false,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const { searchParams, setSearchParams } = useKolDetailRoute()
  const { width } = useWindowSize()
  const [page, setPage] = useState(1)
  const { data: mentionedKols, isLoading } = useMentionedKols({
    kolId,
    kolDcIdHashed,
    platform,
    page,
    duration,
    perPage: width <= 980 ? 5 : 6,
  })
  const level = (searchParams.data?.level ?? 1) + 1

  return (
    <KolCardContainer>
      {((): ReactNode => {
        const hasData = !!mentionedKols?.data && mentionedKols?.data.length > 0

        if (isLoading) {
          return (
            <Flex
              align='center'
              justify='center'
              style={{ minHeight: 200, width: '100%' }}
            >
              <Spin />
            </Flex>
          )
        }

        if (!hasData) {
          return (
            <EmptyWrapper>
              <Empty
                description={
                  <span>
                    {formatMessage({ id: 'autoreport:chart_label_empty_data' })}
                  </span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </EmptyWrapper>
          )
        }

        return (
          <>
            {mentionedKols.data.map((mentionedBrand) => {
              const kolUrl = routes.kolId.stringifyUrl(
                {
                  kolId: mentionedBrand.uuid,
                },
                {
                  country_code: searchParams.data?.country_code,
                  from: 'relatedInfluencer',
                  fromKolId: kolId,
                  fromKolDcIdHashed: kolDcIdHashed,
                  fromKolName: kolName,
                  level,
                },
              )
              const isMockData =
                mentionedBrand.uuid.includes(MOCK_KOL_INFO_PREFIX)
              const handleKolCardClick = (): void => {
                ampli.clickRelatedKol({
                  kolId,
                  kolName,
                })
              }
              return (
                <KolCardWrapper
                  blur={!showMockData && isMockData}
                  isMockData={isMockData}
                  key={mentionedBrand.uuid}
                >
                  <KolThumbnailWrapper
                    href={kolUrl}
                    onClick={handleKolCardClick}
                  >
                    <KolThumbnail
                      imageSize={60}
                      kolUuid={mentionedBrand.uuid}
                    />
                  </KolThumbnailWrapper>
                  <KolCardName href={kolUrl} onClick={handleKolCardClick}>
                    {mentionedBrand.name}
                  </KolCardName>
                  <KolCardFollower>
                    {formatNumber(mentionedBrand.follower, {
                      isCompactDisplay: true,
                    })}{' '}
                    {formatMessage({
                      id: 'kol:followers',
                    })}
                  </KolCardFollower>
                  <KolCardMentionCount
                    onClick={async (): Promise<void> => {
                      const [postStartDate, postEndDate] =
                        getDateRangeFromETLAt(mentionedBrand.etlAt, duration)

                      await setSearchParams(
                        {
                          ...searchParams.data,
                          ...initialPostFilterConditions,
                          keyword: mentionedBrand.platformName,
                          postListPlatform: platform,
                          postListDateRangeStart: postStartDate.toISOString(),
                          postListDateRangeEnd: postEndDate.toISOString(),
                        },
                        'postList',
                      )
                    }}
                  >
                    {formatMessage(
                      {
                        id: 'kol:mention_count',
                      },
                      { count: formatNumber(mentionedBrand.mentionCount) },
                    )}
                  </KolCardMentionCount>
                </KolCardWrapper>
              )
            })}
            <LeftButton
              disabled={page === 1}
              onClick={(): void => {
                setPage(page - 1)
              }}
            />
            <RightButton
              disabled={page >= mentionedKols.totalPage}
              onClick={(): void => {
                setPage(page + 1)
              }}
            />
          </>
        )
      })()}
    </KolCardContainer>
  )
}

const KolThumbnailWrapper = styled(SafeNewTabLink)`
  display: flex;
  justify-content: center;
`
const KolCardName = styled(SafeNewTabLink)`
  font-weight: 600;
  font-size: 16px;
  line-height: 1.4;
  word-break: normal;
  overflow-wrap: anywhere;
  color: ${({ theme }): string => theme.colors.gray10};
`
const KolCardFollower = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }): string => theme.colors.base.grey.grey3};
`
const KolCardMentionCount = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }): string => theme.colors.brand.primary};
  text-decoration-line: underline;
  cursor: pointer;
`
const KolCardWrapper = styled.div<{ blur: boolean; isMockData: boolean }>`
  display: flex;
  align-items: center;
  text-align: center;
  flex-direction: column;
  width: 100%;
  max-width: 188px;
  padding: 8px 16px;
  position: relative;

  ${KolThumbnail} {
    margin-bottom: 8px;
  }
  ${KolCardFollower} {
    margin-bottom: 5px;
  }

  ${({ blur }): FlattenSimpleInterpolation =>
    blur
      ? css`
          filter: blur(6px);

          ::after {
            content: '';
            position: absolute;
            inset: 0;
            background-color: #ffffff88;
          }
        `
      : css``}

  ${({ isMockData }): FlattenSimpleInterpolation =>
    isMockData
      ? css`
          pointer-events: none;
        `
      : css``}
`

const Button = styled(ButtonBase)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  font-size: 12px;
  padding: 0;
`

const LeftButton = styled(Button).attrs({ children: <LeftOutlined /> })``

const RightButton = styled(Button).attrs({ children: <RightOutlined /> })``

const KolCardContainer = styled.div`
  display: flex;
  position: relative;

  ${KolCardWrapper} +${KolCardWrapper} {
    border-left: solid 1px
      ${({ theme }): string => theme.colors.base.grey.grey6};
  }

  ${LeftButton} {
    position: absolute;
    left: -14px;
    top: 50%;
    transform: translateY(-50%);
  }
  ${RightButton} {
    position: absolute;
    right: -14px;
    top: 50%;
    transform: translateY(-50%);
  }
`
const EmptyWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 180px;
`

export default MentionedKolList
